import { Dialog, useDialogManager } from '../../../../../widgets/dialog';
import { FormControl } from '../../../../../widgets/formControl';
import { Input } from '../../../../../widgets/input';
import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import RemoveIcon from '../icons/removeIcon.svg';
import RenameIcon from '../icons/renameIcon.svg';
import SaveIcon from '../icons/saveIcon.svg';
import OverlayPanel from '../overlayPanel';
import { ToolGrid } from '../toolsgrid';
import { TreeHandle } from '../TreeComponent';
import CameraSettingOverlayComponent from './overlay/CameraSettingOverlayComponent';
import { CameraProperty as SceneToolbarProperty } from './cameraEditorConfig';
import { BaseTreeNode, useTreeNodeManager } from './hooks/useTreeNodeManager';

const CameraSettings = styled.div`
    padding: 20px 0px;
    background-color: #f6f8f9;
    border-bottom: 1px solid #dfe2e5;
    cursor: pointer;
    display: flex;
`;

const CameraListItem = styled.div<{ $isSelected?: boolean }>`
    padding: 10px;
    margin: 5px 0;
    background-color: ${(props) => (props.$isSelected ? '#e3f2fd' : '#fff')};
    border: 1px solid ${(props) => (props.$isSelected ? '#2196f3' : '#ddd')};
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;

    &:hover {
        background-color: ${(props) => (props.$isSelected ? '#e3f2fd' : '#f5f5f5')};
        border-color: ${(props) => (props.$isSelected ? '#2196f3' : '#bbb')};
    }
`;

const PlayButton = styled.button`
    background: #dc6843;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    font-size: 12px;

    &:hover {
        background: #e55a00;
    }

    &::before {
        content: '▶';
    }
`;

const CameraListContainer = styled.div`
    padding: 10px;
`;

const CameraListTitle = styled.h3`
    margin: 0 0 12px 0;
    color: #333;
`;

const CameraInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const CameraName = styled.span`
    font-weight: bold;
`;

const CameraMeta = styled.span`
    font-size: 12px;
    color: #888;
`;

const CameraActions = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
`;

const CameraSpecs = styled.div`
    font-size: 0.9em;
    color: #555;
`;

const SelectionInfo = styled.div`
    padding: 10px;
    background-color: #f0f4ff;
    border: 1px solid #d1e7dd;
    border-radius: 4px;
    margin: 10px 0;
    font-size: 14px;
    color: #333;
`;

// Estendi BaseTreeNode per includere le proprietà specifiche della camera
export interface SceneCameraNode extends BaseTreeNode {
    data: {
        position: [number, number, number];
        target: [number, number, number];
        fov: number;
        near: number;
        far: number;
        isActive: boolean;
        enableZoomLimit: boolean;
        enableMinZoom: boolean;
        enableMaxZoom: boolean;
        minZoom: number;
        maxZoom: number;
        height: number;
        enableHorizontalLimit: boolean;
        horizontalMin: number;
        horizontalMax: number;
        enableVerticalLimit: boolean;
        verticalMin: number;
        verticalMax: number;
        // Nuove proprietà per CameraSettingOverlayComponent
        useMinZoomPerc: boolean;
        useMaxZoomPerc: boolean;
        limitLowerRadius: boolean;
        minZoomPerc: number;
        maxZoomPerc: number;
        lowerBetaLimits: number | undefined;
        upperBetaLimits: number | undefined;
        lowerAlphaLimit: number | undefined;
        upperAlphaLimit: number | undefined;
    };
    nodeType?: 'camera' | 'light' | 'object' | string;
    // Proprietà per controllare il comportamento del tree
    disableOverlay?: boolean;
    notTurnableIntoFolder?: boolean;
    // Riferimento all'oggetto camera location originale
    originalCameraLocation?: any;
}

interface CameraEditorProps {
    treeRef: React.RefObject<TreeHandle>;
    cameraLocations?: any[];
    selectedCameraLocations?: any[];
    sceneEditor?: any;
    zkScene?: any;
    onCameraLocationsUpdated?: (cameraLocations: any[]) => void;
}

const CameraEditor: FC<CameraEditorProps> = ({
    treeRef,
    cameraLocations = [],
    selectedCameraLocations = [],
    sceneEditor,
    zkScene,
    onCameraLocationsUpdated
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [showOverlay, setShowOverlay] = useState(false);
    const [playedCamera, setPlayedCamera] = useState<string | null>(null);

    // Funzione per convertire le camera locations del zkScene in nodi per il tree
    const convertCameraLocationToNode = useCallback((cameraLocation: any): SceneCameraNode => {
        const name = cameraLocation?.get ? cameraLocation.get('name') : cameraLocation?.name || 'Unnamed Camera';
        const id = cameraLocation?.get ? cameraLocation.get('locationID') : cameraLocation?.id;
        const nodeId = id ? id.toString() : `camera-${Date.now()}`;

        return {
            id: nodeId,
            name,
            nodeType: 'camera',
            data: {
                position: [0, 0, 1], // Valori di default, potrebbero essere estratti dalle proprietà
                target: [0, 0, 0],
                fov: 1,
                near: 0.1,
                far: 1000,
                isActive: false,
                enableZoomLimit: false,
                enableMinZoom: false,
                enableMaxZoom: false,
                minZoom: 0,
                maxZoom: 10,
                height: 0,
                enableHorizontalLimit: false,
                horizontalMin: -90,
                horizontalMax: 90,
                enableVerticalLimit: false,
                verticalMin: -45,
                verticalMax: 45,
                // Nuove proprietà per CameraSettingOverlayComponent
                useMinZoomPerc: false,
                useMaxZoomPerc: false,
                limitLowerRadius: false,
                minZoomPerc: 0,
                maxZoomPerc: 100,
                lowerBetaLimits: undefined,
                upperBetaLimits: undefined,
                lowerAlphaLimit: undefined,
                upperAlphaLimit: undefined
            },
            disableOverlay: false,
            notTurnableIntoFolder: true,
            // Manteniamo il riferimento all'oggetto originale per operazioni future
            originalCameraLocation: cameraLocation
        };
    }, []);

    // Converti le camera locations in nodi utilizzabili
    const [realCameraNodes, setRealCameraNodes] = useState<SceneCameraNode[]>([]);

    // Funzione per aggiornare i nodi manualmente
    const refreshCameraNodes = useCallback(() => {
        if (zkScene) {
            const updatedCameraLocations = zkScene.get('cameraLocations')?.models || [];
            const convertedNodes = updatedCameraLocations.map(convertCameraLocationToNode);
            setRealCameraNodes(convertedNodes);
            console.log('📷 Camera nodes refreshed:', convertedNodes);

            // Notifica anche il parent component
            if (onCameraLocationsUpdated) {
                onCameraLocationsUpdated(updatedCameraLocations);
            }
        }
    }, [zkScene, convertCameraLocationToNode, onCameraLocationsUpdated]);

    // Aggiorna i nodi quando cambiano le camera locations
    useEffect(() => {
        const convertedNodes = (cameraLocations || []).map(convertCameraLocationToNode);
        setRealCameraNodes(convertedNodes);
        console.log('📷 CameraEditor updated camera locations:', cameraLocations);
        console.log('📷 Converted to nodes:', convertedNodes);
    }, [cameraLocations, convertCameraLocationToNode]);

    // Aggiorna le impostazioni camera quando cambia il sceneEditor
    useEffect(() => {
        if (sceneEditor?.getSceneConfig) {
            const sceneConfig = sceneEditor.getSceneConfig();
            const cameraConfig = sceneConfig?.get?.('camera');

            if (cameraConfig) {
                setCameraSettings((prev) => ({
                    ...prev,
                    useMinZoomPerc: cameraConfig.get?.('useMinZoomPerc') ?? prev.useMinZoomPerc,
                    useMaxZoomPerc: cameraConfig.get?.('useMaxZoomPerc') ?? prev.useMaxZoomPerc,
                    minZoomPerc: cameraConfig.get?.('minZoomPerc') ?? prev.minZoomPerc,
                    maxZoomPerc: cameraConfig.get?.('maxZoomPerc') ?? prev.maxZoomPerc,
                    lowerBetaLimits: cameraConfig.get?.('lowerBetaLimit') ?? prev.lowerBetaLimits,
                    upperBetaLimits: cameraConfig.get?.('upperBetaLimit') ?? prev.upperBetaLimits,
                    lowerAlphaLimit: cameraConfig.get?.('lowerAlphaLimit') ?? prev.lowerAlphaLimit,
                    upperAlphaLimit: cameraConfig.get?.('upperAlphaLimit') ?? prev.upperAlphaLimit,
                    enableZoomLimit: cameraConfig.get?.('limitLowerRadius') ?? prev.limitLowerRadius
                }));

                console.log('📷 Camera settings loaded from sceneConfig:', cameraConfig);
            }
        }
    }, [sceneEditor]);

    // Funzione che NON crea overlay per le camera locations reali
    const createNodeOverlay = useCallback((node: SceneCameraNode) => {
        // Le camera locations reali non hanno overlay
        return () => null;
    }, []);

    // Usa l'hook per la gestione dei nodi con i dati reali
    const { filter, setFilter, selectedIds, filteredData, findNodeById, treeState, setSelectedIds } = useTreeNodeManager<SceneCameraNode>(
        realCameraNodes,
        createNodeOverlay
    );

    // Gestione della selezione multipla usando l'hook
    const handleCameraSelection = (cameraId: string, event: React.MouseEvent) => {
        if (event.ctrlKey || event.metaKey) {
            // Ctrl/Cmd + click per selezione multipla
            const newSelection = selectedIds.includes(cameraId) ? selectedIds.filter((id) => id !== cameraId) : [...selectedIds, cameraId];
            setSelectedIds(newSelection);
        } else if (event.shiftKey && selectedIds.length > 0) {
            // Shift + click per selezione range
            const lastSelectedIndex = filteredData.findIndex((cam) => cam.id === selectedIds[selectedIds.length - 1]);
            const currentIndex = filteredData.findIndex((cam) => cam.id === cameraId);

            if (lastSelectedIndex !== -1 && currentIndex !== -1) {
                const startIndex = Math.min(lastSelectedIndex, currentIndex);
                const endIndex = Math.max(lastSelectedIndex, currentIndex);
                const rangeIds = filteredData.slice(startIndex, endIndex + 1).map((cam) => cam.id);

                const newSelection = [...selectedIds];
                rangeIds.forEach((id) => {
                    if (!newSelection.includes(id)) {
                        newSelection.push(id);
                    }
                });
                setSelectedIds(newSelection);
            }
        } else {
            // Click normale - selezione singola
            setSelectedIds([cameraId]);
        }
    };

    // Funzione per gestire il click sul play button
    const handlePlayCamera = (cameraLocationId: string) => {
        sceneEditor.setCameraLocation(cameraLocationId);
        console.log(`📷 Play camera id: ${cameraLocationId}`);
        setPlayedCamera(cameraLocationId);
    };

    // Camera settings dal sceneConfig invece di mock data
    const [cameraSettings, setCameraSettings] = useState(() => {
        // Ottieni la configurazione della camera dal sceneEditor
        const sceneConfig = sceneEditor?.getSceneConfig?.();
        const cameraConfig = sceneConfig?.get?.('camera');

        return {
            id: 'camera-settings',
            name: 'Camera Settings',
            position: [0, 0, 5] as [number, number, number],
            target: [0, 0, 0] as [number, number, number],
            near: 0.1,
            far: 1000,
            fov: cameraConfig?.get?.('position') ? cameraConfig?.get?.('position').fov : 1,
            isActive: false,
            enableZoomLimit: cameraConfig?.get?.('zoomEnabled') ?? false,
            enableMinZoom: cameraConfig?.get?.('useMinZoomPerc') ?? false,
            enableMaxZoom: cameraConfig?.get?.('useMaxZoomPerc') ?? false,
            minZoom: 0,
            maxZoom: 10,
            height: 0,
            enableHorizontalLimit: (cameraConfig?.get?.('useLowerAlphaLimit') || cameraConfig?.get?.('useUpperAlphaLimit')) ?? false,
            horizontalMin: cameraConfig?.get?.('lowerAlphaLimit') ?? undefined,
            horizontalMax: cameraConfig?.get?.('upperAlphaLimit') ?? undefined,
            enableVerticalLimit: (cameraConfig?.get?.('useLowerBetaLimit') || cameraConfig?.get?.('useUpperBetaLimit')) ?? false,
            verticalMin: cameraConfig?.get?.('lowerBetaLimit') ?? undefined,
            verticalMax: cameraConfig?.get?.('upperBetaLimit') ?? undefined,
            useMinZoomPerc: cameraConfig?.get?.('useMinZoomPerc') ?? false,
            useMaxZoomPerc: cameraConfig?.get?.('useMaxZoomPerc') ?? false,
            limitLowerRadius: cameraConfig?.get?.('limitLowerRadius') ?? false,
            minZoomPerc: cameraConfig?.get?.('minZoomPerc') ?? 0,
            maxZoomPerc: cameraConfig?.get?.('maxZoomPerc') ?? 100,
            lowerBetaLimits: cameraConfig?.get?.('lowerBetaLimit') ?? undefined,
            upperBetaLimits: cameraConfig?.get?.('upperBetaLimit') ?? undefined,
            lowerAlphaLimit: cameraConfig?.get?.('lowerAlphaLimit') ?? undefined,
            upperAlphaLimit: cameraConfig?.get?.('upperAlphaLimit') ?? undefined
        };
    });

    // Funzioni per gestire l'overlay
    const toggleCameraSettings = () => {
        setShowOverlay((prev) => !prev);
    };

    const closeCameraSettings = () => {
        setShowOverlay(false);
    };

    const handleCameraPropertyChange = (property: SceneToolbarProperty, value?: any) => {
        // Aggiorna lo stato locale in base alla proprietà
        switch (property) {
            case SceneToolbarProperty.CAMERA_POSITION_FOV:
                setCameraSettings((prev) => ({ ...prev, fov: value }));
                sceneEditor?.setCameraPositionFOV?.(value);
                break;
            case SceneToolbarProperty.CAMERA_HEIGHT:
                setCameraSettings((prev) => ({ ...prev, height: value }));
                sceneEditor?.setCameraPositionScale(value);
                break;
            case SceneToolbarProperty.CAMERA_LIMIT_ZOOM:
                setCameraSettings((prev) => ({ ...prev, enableZoomLimit: value }));
                sceneEditor?.setCameraLimitLowerRadius?.(value);
                if (!value) {
                    // Se disattivo zoom limit, disattivo anche tutti i sotto-controlli
                    sceneEditor?.setCameraUseMinZoomPercentage?.(false);
                    sceneEditor?.setCameraUseMaxZoomPercentage?.(false);
                    setCameraSettings((prev) => ({ ...prev, useMinZoomPerc: false, useMaxZoomPerc: false }));
                }
                break;

            case SceneToolbarProperty.CAMERA_ENABLE_LIMIT_MIN_ZOOM:
                setCameraSettings((prev) => ({ ...prev, useMinZoomPerc: value }));
                sceneEditor?.setCameraUseMinZoomPercentage?.(value);
                break;

            case SceneToolbarProperty.CAMERA_ENABLE_LIMIT_MAX_ZOOM:
                setCameraSettings((prev) => ({ ...prev, useMaxZoomPerc: value }));
                sceneEditor?.setCameraUseMaxZoomPercentage?.(value);
                break;

            case SceneToolbarProperty.CAMERA_LIMIT_MIN_ZOOM:
                setCameraSettings((prev) => ({ ...prev, minZoomPerc: value * 100 })); // Converti da 0-1 a 0-100
                sceneEditor?.setCameraMinZoomPercentage?.(value);
                break;

            case SceneToolbarProperty.CAMERA_LIMIT_MAX_ZOOM:
                setCameraSettings((prev) => ({ ...prev, maxZoomPerc: value * 100 })); // Converti da 0-1 a 0-100
                sceneEditor?.setCameraMaxZoomPercentage?.(value);
                break;

            case SceneToolbarProperty.CAMERA_LOCK_HORIZONTAL:
                // Gestisce l'abilitazione/disabilitazione dei limiti orizzontali
                sceneEditor?.setCameraUseLowerAlphaLimit?.(value);
                sceneEditor?.setCameraUseUpperAlphaLimit?.(value);

                // Aggiungi i metodi per il lock/unlock come nel vecchio sistema
                if (value) {
                    sceneEditor?.lockCameraAlphaAngle?.();

                    // Quando attivo, usa i valori esistenti dal sceneConfig o imposta valori di default ragionevoli
                    const sceneConfig = sceneEditor?.getSceneConfig?.();
                    const cameraConfig = sceneConfig?.get?.('camera');

                    const currentLowerAlpha = cameraConfig?.get?.('lowerAlphaLimit');
                    const currentUpperAlpha = cameraConfig?.get?.('upperAlphaLimit');

                    // Imposta valori di default solo se non esistono già, con un range ragionevole
                    const defaultLowerAlpha = currentLowerAlpha ?? -Math.PI; // -180 gradi in radianti
                    const defaultUpperAlpha = currentUpperAlpha ?? Math.PI; // +180 gradi in radianti

                    setCameraSettings((prev) => ({
                        ...prev,
                        lowerAlphaLimit: defaultLowerAlpha,
                        upperAlphaLimit: defaultUpperAlpha
                    }));

                    // Imposta i valori solo se non esistevano già
                    if (currentLowerAlpha == null) {
                        sceneEditor?.setViewerCameraLowerAlphaLimit?.(defaultLowerAlpha);
                    }
                    if (currentUpperAlpha == null) {
                        sceneEditor?.setViewerCameraUpperAlphaLimit?.(defaultUpperAlpha);
                    }
                } else {
                    sceneEditor?.unlockCameraAlphaAngle?.();

                    // Quando disattivo, resetto i valori a undefined per far scomparire i controlli
                    setCameraSettings((prev) => ({
                        ...prev,
                        lowerAlphaLimit: undefined,
                        upperAlphaLimit: undefined
                    }));
                }
                break;

            case SceneToolbarProperty.CAMERA_LOCK_VERTICAL:
                // Gestisce l'abilitazione/disabilitazione dei limiti verticali
                sceneEditor?.setCameraUseLowerBetaLimit?.(value);
                sceneEditor?.setCameraUseUpperBetaLimit?.(value);

                // Aggiungi i metodi per il lock/unlock come nel vecchio sistema
                if (value) {
                    sceneEditor?.lockCameraBetaAngle?.();

                    // Quando attivo, usa i valori esistenti dal sceneConfig o imposta valori di default ragionevoli
                    const sceneConfig = sceneEditor?.getSceneConfig?.();
                    const cameraConfig = sceneConfig?.get?.('camera');

                    const currentLowerBeta = cameraConfig?.get?.('lowerBetaLimit');
                    const currentUpperBeta = cameraConfig?.get?.('upperBetaLimit');

                    // Imposta valori di default solo se non esistono già, con un range ragionevole
                    const defaultLowerBeta = currentLowerBeta ?? (0 + 90) * (Math.PI / 180); // -90 gradi in radianti
                    const defaultUpperBeta = currentUpperBeta ?? (180 + 90) * (Math.PI / 180); // +90 gradi in radianti

                    setCameraSettings((prev) => ({
                        ...prev,
                        lowerBetaLimits: defaultLowerBeta,
                        upperBetaLimits: defaultUpperBeta
                    }));

                    // Imposta i valori solo se non esistevano già
                    if (currentLowerBeta == null) {
                        sceneEditor?.setViewerCameraLowerBetaLimit?.(defaultLowerBeta);
                    }
                    if (currentUpperBeta == null) {
                        sceneEditor?.setViewerCameraUpperBetaLimit?.(defaultUpperBeta);
                    }
                } else {
                    sceneEditor?.unlockCameraBetaAngle?.();

                    // Quando disattivo, resetto i valori a undefined per far scomparire i controlli
                    setCameraSettings((prev) => ({
                        ...prev,
                        lowerBetaLimits: undefined,
                        upperBetaLimits: undefined
                    }));
                }
                break;

            case SceneToolbarProperty.CAMERA_MIN_ALPHA:
                setCameraSettings((prev) => ({ ...prev, lowerAlphaLimit: value }));
                sceneEditor?.setViewerCameraLowerAlphaLimit?.(value);
                sceneEditor?.setCameraUseLowerAlphaLimit?.(true);
                break;

            case SceneToolbarProperty.CAMERA_MAX_ALPHA:
                setCameraSettings((prev) => ({ ...prev, upperAlphaLimit: value }));
                sceneEditor?.setViewerCameraUpperAlphaLimit?.(value);
                sceneEditor?.setCameraUseUpperAlphaLimit?.(true);
                break;

            case SceneToolbarProperty.CAMERA_MIN_BETA:
                setCameraSettings((prev) => ({ ...prev, lowerBetaLimits: value }));
                sceneEditor?.setViewerCameraLowerBetaLimit?.(value);
                sceneEditor?.setCameraUseLowerBetaLimit?.(true);
                break;

            case SceneToolbarProperty.CAMERA_MAX_BETA:
                setCameraSettings((prev) => ({ ...prev, upperBetaLimits: value }));
                sceneEditor?.setViewerCameraUpperBetaLimit?.(value);
                sceneEditor?.setCameraUseUpperBetaLimit?.(true);
                break;

            default:
                console.log(`📷 Camera property '${property}' not handled`);
        }

        console.log('📷 Camera property changed:', property, value);
    };

    const tools = [
        {
            name: 'SAVE',
            tooltip: 'Salva posizione camera corrente',
            icon: <SaveIcon />,
            onClick: () => {
                closeCameraSettings();
                let formData = { name: '' };

                showDialog(
                    'save-camera-dialog',
                    <Dialog
                        title='Save Camera Location'
                        buttons={[
                            {
                                label: 'Save',
                                onClick: () => {
                                    const val = formData.name.trim();
                                    if (!val) return;

                                    // Usa sceneEditor.saveCameraLocation per salvare la posizione corrente
                                    if (sceneEditor && sceneEditor.saveCameraLocation) {
                                        console.log('📷 Saving camera location:', val);
                                        sceneEditor.saveCameraLocation(val);

                                        // Aggiorna immediatamente i nodi
                                        setTimeout(() => {
                                            refreshCameraNodes();
                                        }, 100); // Piccolo delay per permettere al sistema di aggiornare
                                    } else {
                                        console.warn('📷 sceneEditor.saveCameraLocation not available');
                                    }

                                    closeDialog('save-camera-dialog');
                                }
                            }
                        ]}
                    >
                        <FormControl label='Camera Location Name'>
                            <Input
                                placeholder='Enter camera location name...'
                                fullWidth
                                onChange={(e) => {
                                    formData.name = e.target.value;
                                }}
                            />
                        </FormControl>
                    </Dialog>
                );
            }
        },
        {
            name: 'RENAME',
            tooltip: 'Rinomina camera location selezionata',
            icon: <RenameIcon />,
            onClick: () => {
                closeCameraSettings();
                if (!selectedIds.length) return;

                // Se è selezionata una sola camera, permetti il rename
                if (selectedIds.length === 1) {
                    treeState.closeOverlay();
                    const idToRename = selectedIds[0];
                    const currNode = findNodeById(idToRename);
                    if (!currNode) return;
                    let formData = { name: currNode.name };

                    showDialog(
                        'rename-dialog',
                        <Dialog
                            title='Rename Camera Location'
                            buttons={[
                                {
                                    label: 'Rename',
                                    onClick: () => {
                                        const newName = formData.name.trim();
                                        if (newName && currNode.originalCameraLocation) {
                                            // Per rinominare: rimuovi la vecchia e salva con il nuovo nome
                                            // Prima salviamo la posizione corrente con il nuovo nome
                                            if (sceneEditor && sceneEditor.saveCameraLocation && sceneEditor.removeCameraLocation) {
                                                console.log('📷 Renaming camera location:', currNode.name, '->', newName);

                                                // Rimuovi la vecchia camera location
                                                sceneEditor.removeCameraLocation(currNode.originalCameraLocation);

                                                // Salva con il nuovo nome (usando la posizione corrente)
                                                sceneEditor.saveCameraLocation(newName);

                                                // Aggiorna immediatamente i nodi
                                                setTimeout(() => {
                                                    refreshCameraNodes();
                                                }, 100);
                                            }
                                            closeDialog('rename-dialog');
                                        }
                                    }
                                }
                            ]}
                        >
                            <FormControl label='Camera Location Name'>
                                <Input
                                    placeholder='Camera location name...'
                                    fullWidth
                                    defaultValue={currNode.name}
                                    onChange={(e) => {
                                        formData.name = e.target.value;
                                    }}
                                />
                            </FormControl>
                        </Dialog>
                    );
                } else {
                    // Se sono selezionate più camere, mostra un messaggio
                    alert('Please select only one camera location to rename');
                }
            }
        },
        {
            name: 'REMOVE',
            tooltip: 'Rimuovi camera location selezionata',
            icon: <RemoveIcon />,
            onClick: () => {
                closeCameraSettings();
                if (!selectedIds.length) return;
                treeState.closeOverlay();

                const cameraText = selectedIds.length === 1 ? 'Camera Location' : 'Camera Locations';
                const countText = selectedIds.length === 1 ? '' : ` (${selectedIds.length})`;

                showDialog(
                    'remove-dialog',
                    <Dialog
                        title={`Remove ${cameraText}${countText}`}
                        showCloseButton={false}
                        buttons={[
                            {
                                label: 'Cancel',
                                onClick: () => closeDialog('remove-dialog')
                            },
                            {
                                label: 'Remove',
                                onClick: () => {
                                    // Rimuovi le camera locations dal SceneEditor
                                    selectedIds.forEach((id) => {
                                        const node = findNodeById(id);
                                        if (node?.originalCameraLocation && sceneEditor?.removeCameraLocation) {
                                            console.log('📷 Removing camera location:', node.name);
                                            sceneEditor.removeCameraLocation(node.originalCameraLocation);
                                        }
                                    });

                                    // Rimuovi dalle selezioni locali
                                    if (selectedIds.includes(playedCamera!)) {
                                        setPlayedCamera(null);
                                    }

                                    // Pulisci la selezione
                                    setSelectedIds([]);

                                    // Aggiorna immediatamente i nodi
                                    setTimeout(() => {
                                        refreshCameraNodes();
                                    }, 100);

                                    closeDialog('remove-dialog');
                                }
                            }
                        ]}
                    >
                        <p>Are you sure you want to remove the selected {cameraText.toLowerCase()}?</p>
                    </Dialog>
                );
            }
        }
    ];

    return (
        <div>
            <ToolGrid tools={tools} columns={tools.length} />
            <CameraSettings onClick={toggleCameraSettings}>Camera Settings</CameraSettings>

            {/* Lista delle camere usando i dati filtrati dall'hook */}
            <CameraListContainer>
                <CameraListTitle>Cameras ({filteredData.length})</CameraListTitle>
                {filteredData.map((camera) => (
                    <CameraListItem key={camera.id} $isSelected={selectedIds.includes(camera.id)} onClick={(e) => handleCameraSelection(camera.id, e)}>
                        <CameraInfo>
                            <CameraName>{camera.name}</CameraName>
                            {/* <CameraMeta>ID: {camera.id}</CameraMeta> */}
                            {/* <CameraMeta>Active: {playedCamera === camera.id ? 'Yes' : 'No'}</CameraMeta> */}
                        </CameraInfo>
                        <CameraActions>
                            {/* <CameraSpecs>
								FOV: {camera.fov}° | Exposure: {camera.exposure}
								altre info qui?
							</CameraSpecs> */}
                            <PlayButton onClick={(e) => handlePlayCamera(camera.originalCameraLocation.get('locationID'))} title={`Activate ${camera.name}`} />
                        </CameraActions>
                    </CameraListItem>
                ))}
            </CameraListContainer>

            {/* Overlay per le impostazioni camera */}
            {showOverlay && (
                <OverlayPanel onClose={closeCameraSettings}>
                    <div style={{ padding: '10px' }}>
                        <h3>Camera Settings</h3>
                        <CameraSettingOverlayComponent camera={cameraSettings} onPropertyChange={handleCameraPropertyChange} />
                    </div>
                </OverlayPanel>
            )}
        </div>
    );
};

export default CameraEditor;
